// ManageEventSchedule.js
import { React, useState, useEffect } from 'react'
import { motion } from "framer-motion";
import { useParams } from "react-router-dom";
import { Helmet } from 'react-helmet'
import { v4 as uuidv4 } from 'uuid'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronRight, faChevronDown, faChevronUp, faCopy, faTrash, faPlus, faClock } from '@fortawesome/free-solid-svg-icons'

import MainHeader from '../../../components/MainHeader/MainHeader.js'
import MainFooter from '../../../components/MainFooter/MainFooter.js'
import Skeleton from '../../../components/Skeleton/Skeleton.js'
import CTAButton from '../../../components/CTAButton/CTAButton.js'

import { boxvar2_1, boxvar4, URL_MAINAPI, API_TOKEN, getWeekdayFromScheduleDay } from '../../../main.js'

import '../../../main.css'
import './ManageEventSchedule.css'
import './MManageEventSchedule.css'

import Cookies from 'universal-cookie'

export default function ManageEventSchedule() {

    const cookies = new Cookies()
    let cur_userid = cookies.get('userid')
    let cur_authtoken = cookies.get('authtoken')

    const { eventid } = useParams();
    const [loadedEventData, setLoadedEventData] = useState(0)
    const [isNew, setIsNew] = useState(0)
    const [loadedEventScheduleData, setLoadedEventScheduleData] = useState(0)
    const [resError, setResError] = useState("")
    const [planChanged, setPlanChanged] = useState(0)

    const [eventData, setEventData] = useState({
        name: "Event wird geladen...",
        active: 0
    })

    const [eventScheduleData, setEventScheduleData] = useState({
        eventId: "0",
        dates: []
    });

    useEffect(() => {
        window.onbeforeunload = function () { if (planChanged) return "Willst du wirklich abbrechen? Du musst vorher absenden"; };

        if (eventid && eventid !== "0") {
            getEventData()
            getEventScheduleData()
        }

        return () => { window.onbeforeunload = null }
    }, [])

    function getEventData() {
        setLoadedEventData(0)
        fetch(URL_MAINAPI + "/event/" + eventid + "?ignoreinactive=1", {
            method: "GET",
            headers: { 'Content-Type': 'application/json', 'APITOKEN': API_TOKEN, 'authtoken': cur_authtoken }
        })
            .then(r => r.json().then(d => ({ status: r.status, data: d })))
            .then(res => {
                if (res.status === 200) {
                    setEventData(res.data)
                    setLoadedEventData(1)
                } else {
                    setEventData({ name: "Fehler" })
                    setResError(res.data?.error || "Fehler beim Laden")
                    setLoadedEventData(0)
                }
            }).catch(err => { setResError(err.message); setLoadedEventData(0) })
    }

    function getEventScheduleData() {
        setLoadedEventScheduleData(0)
        setIsNew(0)

        fetch(URL_MAINAPI + "/eventschedule/" + eventid, {
            method: "GET",
            headers: { 'Content-Type': 'application/json', 'APITOKEN': API_TOKEN, 'authtoken': cur_authtoken }
        })
            .then(r => r.json().then(d => ({ status: r.status, data: d })))
            .then(res => {
                if (res.status === 200) {
                    setLoadedEventScheduleData(1)
                    applyEventSchedulaData(res.data)

                } else if (res.status === 404) {
                    setLoadedEventScheduleData(1)
                    setIsNew(1)

                }

                else {
                    setResError(res.data?.error || "Fehler beim Laden der Pläne")
                    setLoadedEventScheduleData(0)
                }
            }).catch(err => { setResError(err.message); setLoadedEventScheduleData(0) })
    }

    function applyEventSchedulaData(scheduledata) {
        // Map incoming shape to our editable shape, add _localId for stable editing
        const mapped = {
            eventId: scheduledata.eventid || eventid,
            dates: (scheduledata.dates || [])
                .sort((a, b) => new Date(a.day.split('.').reverse().join('-')) - new Date(b.day.split('.').reverse().join('-'))) // Sortieren nach "YYYY-MM-DD" Format; Aufsteigend
                .map(date => ({
                    _localId: uuidv4(),
                    day: date.day,
                    dateId: date._id,
                    schedules: (date.schedules || []).map(schedule => ({
                        _localId: uuidv4(),
                        scheduleElementId: schedule._id,
                        title: schedule.title,
                        startTime: schedule.startTime,
                        endTime: schedule.endTime,
                        description: schedule.description,
                        location: schedule.location,
                        dutys: (schedule.dutys || []).map(duty => ({
                            _localId: uuidv4(),
                            dutyId: duty._id,
                            title: duty.title,
                            description: duty.description,
                            maxPersons: duty.maxPersons || 0
                        }))
                    }))
                }))
        }
        setEventScheduleData(mapped)
    }

    /* -------------------------
       CRUD helpers (client-side)
       ------------------------- */

    function markChanged() { if (!planChanged) setPlanChanged(1) }

    function addDate() {
        const newDate = { _localId: uuidv4(), day: new Date().toISOString().slice(0, 10).split('-').reverse().join('.'), schedules: [] }
        setEventScheduleData(prev => ({ ...prev, dates: [...prev.dates, newDate] }))
        markChanged()
    }

    function removeDate(dateLocalId) {
        if (!window.confirm("Datum wirklich löschen? Alle darin enthaltenen Pläne gehen verloren.")) return
        setEventScheduleData(prev => ({ ...prev, dates: prev.dates.filter(d => d._localId !== dateLocalId) }))
        markChanged()
    }

    function duplicateDate(dateLocalId) {
        const dateToDuplicate = eventScheduleData.dates.find(d => d._localId === dateLocalId);
        if (!dateToDuplicate) return; // Falls Date nicht gefunden abbrechen

        const newDate = {
            _localId: uuidv4(),
            day: dateToDuplicate.day + " (Kopie)",
            schedules: dateToDuplicate.schedules.map(schedule => ({
                _localId: uuidv4(),
                title: schedule.title,
                startTime: schedule.startTime,
                endTime: schedule.endTime,
                description: schedule.description,
                location: schedule.location,
                dutys: schedule.dutys.map(duty => ({
                    _localId: uuidv4(),
                    title: duty.title,
                    description: duty.description,
                    maxPersons: duty.maxPersons
                }))
            }))
        };

        setEventScheduleData(prev => ({ ...prev, dates: [...prev.dates, newDate] }));
        markChanged();
    }

    function addSchedule(dateLocalId) {
        const newSchedule = {
            _localId: uuidv4(),
            title: "Neuer Zeitblock",
            startTime: "08:00",
            endTime: "12:00",
            description: "",
            location: "",
            dutys: []
        }
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? { ...d, schedules: [...d.schedules, newSchedule] } : d)
        }))
        markChanged()
    }

    function removeSchedule(dateLocalId, scheduleLocalId) {
        if (!window.confirm("Schedule wirklich löschen?")) return
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? { ...d, schedules: d.schedules.filter(s => s._localId !== scheduleLocalId) } : d)
        }))
        markChanged()
    }

    function addDuty(dateLocalId, scheduleLocalId) {
        const newDuty = { _localId: uuidv4(), title: "Neuer Dienst", description: "", maxPersons: 1 }
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? {
                ...d,
                schedules: d.schedules.map(s => s._localId === scheduleLocalId ? { ...s, dutys: [...s.dutys, newDuty] } : s)
            } : d)
        }))
        markChanged()
    }

    function removeDuty(dateLocalId, scheduleLocalId, dutyLocalId) {
        if (!window.confirm("Dienst wirklich löschen?")) return
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? {
                ...d,
                schedules: d.schedules.map(s => s._localId === scheduleLocalId ? { ...s, dutys: s.dutys.filter(dt => dt._localId !== dutyLocalId) } : s)
            } : d)
        }))
        markChanged()
    }

    /* Generic field updates */
    function updateDateField(dateLocalId, field, value) {
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? { ...d, [field]: value } : d)
        }))
        markChanged()
    }
    function updateScheduleField(dateLocalId, scheduleLocalId, field, value) {
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? {
                ...d,
                schedules: d.schedules.map(s => s._localId === scheduleLocalId ? { ...s, [field]: value } : s)
            } : d)
        }))
        markChanged()
    }
    function updateDutyField(dateLocalId, scheduleLocalId, dutyLocalId, field, value) {
        setEventScheduleData(prev => ({
            ...prev,
            dates: prev.dates.map(d => d._localId === dateLocalId ? {
                ...d,
                schedules: d.schedules.map(s => s._localId === scheduleLocalId ? {
                    ...s,
                    dutys: s.dutys.map(dt => dt._localId === dutyLocalId ? { ...dt, [field]: value } : dt)
                } : s)
            } : d)
        }))
        markChanged()
    }


    async function updateSchedule() {
        try {
            if (!eventScheduleData) return

            setResError("")

            const payload = {
                eventid: eventid,
                dates: eventScheduleData.dates.map(d => ({
                    ...(d.dateId && { _id: d.dateId }), // Include existing date ID if present
                    day: (d.day || "").toString().trim(),
                    schedules: (d.schedules || []).map(s => ({
                        ...(s.scheduleElementId && { _id: s.scheduleElementId }), // Include existing schedule ID if present
                        title: (s.title || "").toString().trim(),
                        startTime: (s.startTime || "").toString().trim(),
                        endTime: (s.endTime || "").toString().trim(),
                        description: (s.description || "").toString().trim(),
                        location: (s.location || "").toString().trim(),
                        dutys: (s.dutys || []).map(dt => ({
                            ...(dt.dutyId && { _id: dt.dutyId }), // Include existing duty ID if present
                            title: (dt.title || "").toString().trim(),
                            description: (dt.description || "").toString().trim(),
                            maxPersons: Number(dt.maxPersons || 0)
                        }))
                    }))
                }))
            }

            if (isNew == 1) {
                // create
                const res = await fetch(URL_MAINAPI + "/eventschedule/create", {
                    method: "POST",
                    headers: { 'Content-Type': 'application/json', 'APITOKEN': API_TOKEN, 'authtoken': cur_authtoken },
                    body: JSON.stringify(payload)
                })
                const data = await res.json()
                if (res.status === 200) {
                    setPlanChanged(0)
                    // after create, re-fetch to sync IDs
                    getEventScheduleData()
                } else {
                    setResError(data?.error || JSON.stringify(data))
                }
            } else {
                // update (replace)
                const res = await fetch(URL_MAINAPI + "/eventschedule/" + eventid, {
                    method: "PATCH",
                    headers: { 'Content-Type': 'application/json', 'APITOKEN': API_TOKEN, 'authtoken': cur_authtoken },
                    body: JSON.stringify(payload)
                })
                const data = await res.json()
                if (res.status === 200) {
                    setPlanChanged(0)
                    getEventScheduleData()
                } else {
                    setResError(data?.error || JSON.stringify(data))
                }
            }
        } catch (err) {
            setResError(err.message)
        }
    }

    function resetChanges() {
        getEventScheduleData()
        setPlanChanged(0)
        setResError("")
    }

    var [isOpen, setIsOpen] = useState(1)
    function toggleDetails() {
        // Select all <details> elements in the document
        const detailsElements = document.querySelectorAll('.schedule_date');
        detailsElements.forEach(detail => {
            detail.open = !isOpen; // Sets the open property to true, expanding the element
        });
        setIsOpen(!isOpen);
    };

    return (
        <div className='mainCon page' id='mainCon_manageschedule'>
            <MainHeader selectedsite={""} />
            <Helmet>
                <meta charSet="utf-8" />
                <title>Zeitplan verwalten</title>
                <meta property="og:title" content="Zeitplanverwaltung" />
                <meta property="og:description" content="Verwalte den Zeitplan für deine Veranstaltung" />
                <meta name="robots" content="noindex" />
            </Helmet>

            <main className='page-body container section' style={{ paddingTop: '36px' }}>
                <div className='breadcrumb'>
                    {eventData.orgid ? <><a href={'/manage/org/' + eventData.orgid}>{eventData.organization ? eventData.organization.name : 'Organisation'}</a><FontAwesomeIcon icon={faChevronRight} /></> : <><a href='/profil'>Profil</a><FontAwesomeIcon icon={faChevronRight} /></>}
                    {loadedEventData ? <><a href={'/manage/event/' + eventid}>{eventData.name || 'Event'}</a><FontAwesomeIcon icon={faChevronRight} /></> : null}
                    <span>Zeitplan</span>
                </div>

                <motion.h1
                    variants={boxvar4}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: false, margin: "-50px" }}
                    className='section-title'
                    style={{ marginBottom: '6px' }}
                >
                    🗓️ Zeitplan verwalten
                </motion.h1>
                {loadedEventData ? <p className='section-sub'>für <a href={"/manage/event/" + eventid}>{eventData.name}</a></p> : <Skeleton width="240px" />}

                {(eventData.active != 1) ? (
                    <div className='banner-info' style={{ background: 'var(--warn-soft)', color: 'var(--warn)', marginBottom: '16px', marginTop: '16px' }}>
                        ⚠️ Event ist inaktiv und für andere nicht aufrufbar!
                    </div>
                ) : null}

                {resError ? (
                    <div className='banner-info' style={{ background: 'var(--error-soft)', color: 'var(--error)', marginBottom: '16px', marginTop: '16px' }}>
                        Fehler: {resError}
                    </div>
                ) : null}

                <motion.div variants={boxvar2_1} initial="hidden" whileInView="visible" viewport={{ once: false, margin: "-50px" }} style={{ marginTop: '20px' }}>
                    <div className='card' style={{ padding: '24px 26px', marginBottom: '20px' }}>
                        <div className='schedule-toolbar'>
                            <button
                                type='button'
                                className={'toggle-chip' + (isOpen ? ' toggle-chip-active' : '')}
                                aria-pressed={isOpen ? 'true' : 'false'}
                                title={(isOpen) ? "Alle Tage zuklappen" : "Alle Tage aufklappen"}
                                onClick={() => toggleDetails()}
                            >
                                <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} />
                                <span>{(isOpen) ? "Zuklappen" : "Aufklappen"}</span>
                            </button>

                            <CTAButton variant="secondary" hideArrow={true} title="Datum hinzufügen" onclick={() => addDate()} />

                            <CTAButton variant="primary" hideArrow={false} title={isNew ? "Erstellen" : planChanged ? "Speichern" : "Gespeichert"} onclick={() => updateSchedule()} />
                        </div>

                        {(planChanged) ? (
                            <div className='schedule-changed-hint'>
                                Ungesicherte Änderungen! <button type='button' className='schedule-changed-hint-link' onClick={() => resetChanges()}>Klick zum Verwerfen</button>
                            </div>
                        ) : null}
                    </div>

                    <div className='schedule_dates_list'>
                        {eventScheduleData.dates.length === 0 ? (
                            <div className='card schedule-empty' style={{ padding: '28px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                Keine Termine vorhanden. Klicke "Datum hinzufügen".
                            </div>
                        ) : null}

                        {eventScheduleData.dates.map(date => {
                            const weekdayLabel = getWeekdayFromScheduleDay(date.day);
                            return (
                                <details className='card schedule_date' key={date._localId}>
                                    <summary>
                                        <b>📅 Datum{weekdayLabel ? ` (${weekdayLabel})` : ''}</b>
                                        <div className='schedule_date_header'>
                                            <input type="text" className='schedule_date_input' placeholder="01.01.2001" autoComplete='on' value={date.day} onChange={(e) => updateDateField(date._localId, 'day', e.target.value)}></input>
                                            <button type='button' className='icon-action' title="Duplizieren" onClick={(e) => { e.preventDefault(); e.stopPropagation(); duplicateDate(date._localId); }}><FontAwesomeIcon icon={faCopy} /></button>
                                            <button type='button' className='icon-action icon-action-danger' title="Datum löschen" onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeDate(date._localId); }}><FontAwesomeIcon icon={faTrash} /></button>
                                        </div>
                                    </summary>

                                    <div className='schedule_date_body'>
                                        {date.schedules.map(schedule => (
                                            <div className='schedule_timeblock' key={schedule._localId}>
                                                <div className='schedule_timeblock_head'>
                                                    <span className='schedule_timeblock_clock'><FontAwesomeIcon icon={faClock} /></span>
                                                    <div className='schedule_timeblock_head_fields'>
                                                        <div className="field">
                                                            <label>Zeitblock-Titel</label>
                                                            <input type="text" placeholder="Zeitblock-Titel" autoComplete='on' value={schedule.title} onChange={(e) => updateScheduleField(date._localId, schedule._localId, 'title', e.target.value)}></input>
                                                        </div>
                                                        <div className='schedule_timeblock_head_times'>
                                                            <div className="field">
                                                                <label>Startzeit</label>
                                                                <input type="text" placeholder="Startzeit" autoComplete='on' value={schedule.startTime} onChange={(e) => updateScheduleField(date._localId, schedule._localId, 'startTime', e.target.value)}></input>
                                                            </div>
                                                            <div className="field">
                                                                <label>Endzeit</label>
                                                                <input type="text" placeholder="Endzeit" autoComplete='on' value={schedule.endTime} onChange={(e) => updateScheduleField(date._localId, schedule._localId, 'endTime', e.target.value)}></input>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className='schedule_timeblock_actions'>
                                                        <button type='button' className='icon-action icon-action-danger' title="Zeitblock löschen" onClick={() => removeSchedule(date._localId, schedule._localId)}><FontAwesomeIcon icon={faTrash} /></button>
                                                    </div>
                                                </div>

                                                <div className='schedule_timeblock_fields'>
                                                    <div className="field">
                                                        <label>Beschreibung (optional)</label>
                                                        <textarea rows="2" placeholder="Beschreibung (optional)" autoComplete='on' value={schedule.description} onChange={(e) => updateScheduleField(date._localId, schedule._localId, 'description', e.target.value)}></textarea>
                                                    </div>
                                                    <div className="field">
                                                        <label>Ort</label>
                                                        <input type="text" placeholder="Ort" autoComplete='on' value={schedule.location} onChange={(e) => updateScheduleField(date._localId, schedule._localId, 'location', e.target.value)}></input>
                                                    </div>
                                                </div>

                                                <div className='schedule_dutys_list'>
                                                    {schedule.dutys.map(duty => (
                                                        <div className='schedule_duty' key={duty._localId}>
                                                            <div className='schedule_duty_head'>
                                                                <div className="field" style={{ flex: '1 1 200px' }}>
                                                                    <label>Diensttitel</label>
                                                                    <input type="text" placeholder="Diensttitel" autoComplete='on' value={duty.title} onChange={(e) => updateDutyField(date._localId, schedule._localId, duty._localId, 'title', e.target.value)}></input>
                                                                </div>
                                                                <div className="field schedule_duty_max">
                                                                    <label>Max. Helfer</label>
                                                                    <input type="number" title="Maximale Helferzahl" placeholder="1" autoComplete='on' value={duty.maxPersons} onChange={(e) => updateDutyField(date._localId, schedule._localId, duty._localId, 'maxPersons', e.target.value)}></input>
                                                                </div>
                                                                <button type='button' className='icon-action icon-action-danger' title="Dienst löschen" onClick={() => removeDuty(date._localId, schedule._localId, duty._localId)}><FontAwesomeIcon icon={faTrash} /></button>
                                                            </div>
                                                            <div className="field">
                                                                <label>Dienstbeschreibung (optional)</label>
                                                                <textarea rows="2" placeholder="Dienstbeschreibung (optional)" autoComplete='on' value={duty.description} onChange={(e) => updateDutyField(date._localId, schedule._localId, duty._localId, 'description', e.target.value)}></textarea>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>

                                                <div className='schedule_timeblock_footer'>
                                                    <button type='button' className='btn btn-quiet btn-sm' onClick={() => addDuty(date._localId, schedule._localId)}><FontAwesomeIcon icon={faPlus} /> Dienst hinzufügen</button>
                                                </div>
                                            </div>
                                        ))}

                                        <div className='schedule_date_footer'>
                                            <button type='button' className='btn btn-secondary btn-sm' onClick={() => addSchedule(date._localId)}><FontAwesomeIcon icon={faPlus} /> Zeitblock hinzufügen</button>
                                        </div>
                                    </div>
                                </details>
                            );
                        })}
                    </div>
                </motion.div>
            </main>

            <MainFooter useEventStyle={false} />
        </div>
    )
}
